import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { EditClosedDialog, useEditClosedDialog } from './EditClosedDialog';
import { useAuth } from '../../units/auth/AuthProvider';
import { grey } from '@mui/material/colors';
import { Closed } from '../../api/closed';
import moment from 'moment';
import { Store } from '../../api/store';

export const useClosedManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const [closeds, setCloseds] = useState<Closed[]>([]);
    const reloadCloseds = async () => {
        api.closed
            .get({})
            .then(
                (ret) =>
                    ret.success &&
                    setCloseds(ret.data?.sort((a, b) => new Date(a.closedFm!).getTime() - new Date(b.closedFm!).getTime()) || []),
            );
    };
    const sideBar = useSideBar();
    const editClosedDialog = useEditClosedDialog();
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'closedId', headerName: '店面ID', flex: 4, minWidth: 120, cellClassName: 'cell', hide: true },
        {
            field: 'closedFm',
            headerName: '店休時間起',
            flex: 3,
            minWidth: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Closed;
                return <>{moment(row.closedFm).format('YYYY-MM-DD A hh:mm')}</>;
            },
            hide: false,
        },
        {
            field: 'closedTo',
            headerName: '店休時間迄',
            flex: 3,
            minWidth: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Closed;
                return <>{moment(row.closedTo).format('YYYY-MM-DD A hh:mm')}</>;
            },
            hide: false,
        },
        {
            field: 'msg',
            headerName: '公告訊息',
            flex: 7,
            minWidth: 400,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Closed;
                return <>{row.msg}</>;
            },
            hide: false,
        },
        {
            field: 'store',
            headerName: '店面名稱',
            flex: 2,
            minWidth: 120,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Closed;
                return <>{row.store?.storeName}</>;
            },
            hide: false,
        },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>{'操作'}</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Closed;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editClosedDialog.setClosedId(row.closedId || '');
                                editClosedDialog.setInputMsg(row.msg || '');
                                editClosedDialog.setInputClosedFm(new Date(row.closedFm!));
                                editClosedDialog.setInputClosedTo(new Date(row.closedTo!));
                                editClosedDialog.setOnUpdateClosed(
                                    () => (closedId: string, closedFm: Date, closedTo: Date, msg: string, store: Store) => {
                                        api.closed
                                            .update({ closedId: closedId, closedFm: closedFm, closedTo: closedTo, msg: msg, store: store })
                                            .then((ret) => {
                                                if (ret.success) {
                                                    reloadCloseds();
                                                    page.simpleAlert.show({
                                                        autoHideDuration: 2000,
                                                        severity: 'success',
                                                        message: ret.message,
                                                    });
                                                    editClosedDialog.onClose();
                                                } else {
                                                    page.simpleAlert.show({
                                                        autoHideDuration: 6000,
                                                        severity: 'error',
                                                        message: JSON.stringify(ret),
                                                    });
                                                    editClosedDialog.onClose();
                                                }
                                            });
                                    },
                                );
                                editClosedDialog.onOpen();
                            }}
                        >
                            {'編輯'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.closed
                                            .remove({ closedId: row.closedId! })
                                            .finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadCloseds();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            {'刪除'}
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadCloseds();
    }, []);
    return {
        page,
        classes,
        auth,
        closeds,
        setCloseds,
        reloadCloseds,
        sideBar,
        editClosedDialog,
        inputSearchConfig,
        setInputSearchConfig,
        columns,
    };
};

export const ClosedManagePage = (): React.ReactElement => {
    const props = useClosedManagePage();
    return (
        <Page {...props.page} title='店休管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={props.inputSearchConfig}
                                onChange={(e) => props.setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['店休日期', '店面名稱'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={props.classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => {
                                    props.editClosedDialog.setOnCreateClosed(
                                        () => (closedFm: Date, closedTo: Date, msg: string, store: Store) => {
                                            api.closed
                                                .create({ closedFm: closedFm, closedTo: closedTo, msg: msg, store: store })
                                                .then((ret) => {
                                                    if (ret.success) {
                                                        props.reloadCloseds();
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'success',
                                                            message: ret.message,
                                                        });
                                                        props.editClosedDialog.onClose();
                                                    } else {
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 6000,
                                                            severity: 'error',
                                                            message: JSON.stringify(ret),
                                                        });
                                                        props.editClosedDialog.onClose();
                                                    }
                                                });
                                        },
                                    );
                                    props.editClosedDialog.onOpen();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增店休'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = props.inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return props.closeds.filter((v, i, a) =>
                                    [
                                        searchs.length == 0
                                            ? true
                                            : searchs
                                                  .map(
                                                      (s) =>
                                                          moment(v.closedFm!).format('YYYY-MM-DD').includes(s.toUpperCase()) ||
                                                          v.store?.storeName?.toUpperCase().includes(s.toUpperCase()) ||
                                                          false,
                                                  )
                                                  .every((v) => v),
                                    ].every((v) => v),
                                );
                            })()}
                            columns={props.columns}
                            getRowId={(row: Closed) => row.closedId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditClosedDialog {...props.editClosedDialog} />
        </Page>
    );
};
